import { PagesViewSettings } from 'tabview/pagesView/types';
import { DropdownOptionType } from '@ardoq/dropdown-menu';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  MenuMetaOptions,
  type ReferenceTypeOption,
  type TraversedReferenceTypeInfo,
  isTraversedReferenceType,
  matchSelectedReferenceType,
  getAllNoneDropdownGroup,
  getDropdownOptionGroup,
  formatReferenceType,
  getFieldMultiSelectDropdownOptions,
  getRightMenuConfig,
} from '@ardoq/settings-bar';
import {
  type SettingsConfig,
  SettingsType,
  viewSettingsConsts,
  updateViewSettings,
} from '@ardoq/view-settings';
import { IconName } from '@ardoq/icons';
import { APIFieldAttributes, ViewIds } from '@ardoq/api-types';
import { dispatchAction } from '@ardoq/rxbeach';

import type { OnViewSettingsUpdate } from '@ardoq/data-model';
import { addToPresentation } from 'viewSettings/exportHandlers';
import { focusedChromeNow } from 'scope/focusedChromeNow';
import { focusedChromeOperations } from 'scope/focusedChromeOperations';
import { viewHasLegend } from 'views/metaInfoTabs';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';

import { CommonDropdownOptions } from '@ardoq/global-consts';

const VIEW_ID = ViewIds.PAGESVIEW;
const FIELD_SETTINGS_KEY = 'includeFields';

const referenceTypeIsSelected = (
  value: TraversedReferenceTypeInfo,
  selectedReferenceTypes: ReferenceTypeOption[]
) =>
  Boolean(
    selectedReferenceTypes.find(
      selectedReferenceType =>
        isTraversedReferenceType(selectedReferenceType) &&
        matchSelectedReferenceType(value, selectedReferenceType)
    )
  );

const referenceOptionIsActive = (
  referenceOption: ReferenceTypeOption,
  selectedReferenceTypes: ReferenceTypeOption[],
  availableReferenceTypes: TraversedReferenceTypeInfo[]
) => {
  if (
    referenceOption === CommonDropdownOptions.ALL ||
    referenceOption === CommonDropdownOptions.ALL_EXCEPT_PARENT_CHILD
  ) {
    return (
      selectedReferenceTypes.includes(referenceOption) ||
      availableReferenceTypes.every(referenceType =>
        referenceTypeIsSelected(referenceType, selectedReferenceTypes)
      )
    );
  } else if (referenceOption === CommonDropdownOptions.NONE) {
    return selectedReferenceTypes.length === 0;
  }
  return (
    selectedReferenceTypes.includes(CommonDropdownOptions.ALL) ||
    referenceTypeIsSelected(
      referenceOption as TraversedReferenceTypeInfo,
      selectedReferenceTypes
    )
  );
};

const toggleReferenceType = (
  value: ReferenceTypeOption,
  selectedReferenceTypes: ReferenceTypeOption[],
  availableReferenceTypes: TraversedReferenceTypeInfo[]
) => {
  if (value === CommonDropdownOptions.ALL) {
    return selectedReferenceTypes.includes(CommonDropdownOptions.ALL)
      ? []
      : [CommonDropdownOptions.ALL];
  } else if (value === CommonDropdownOptions.NONE) {
    return [];
  } else if (selectedReferenceTypes.includes(CommonDropdownOptions.ALL)) {
    return availableReferenceTypes.filter(
      referenceType =>
        !matchSelectedReferenceType(
          referenceType,
          value as TraversedReferenceTypeInfo
        )
    );
  } else if (
    referenceTypeIsSelected(
      value as TraversedReferenceTypeInfo,
      selectedReferenceTypes as TraversedReferenceTypeInfo[]
    )
  ) {
    return selectedReferenceTypes.filter(
      selectedReferenceType =>
        !matchSelectedReferenceType(
          value as TraversedReferenceTypeInfo,
          selectedReferenceType as TraversedReferenceTypeInfo
        )
    );
  }
  return [...selectedReferenceTypes, value];
};

const getSelectedReferenceOnClick =
  (
    settingsKey: string,
    selectedReferenceTypes: ReferenceTypeOption[],
    availableReferenceTypes: TraversedReferenceTypeInfo[]
  ) =>
  (value: ReferenceTypeOption) => {
    const newSelectedReferenceTypes = toggleReferenceType(
      value,
      selectedReferenceTypes,
      availableReferenceTypes
    );
    const settings = { [settingsKey]: newSelectedReferenceTypes };
    const persistent = true;
    dispatchAction(
      updateViewSettings({
        viewId: VIEW_ID,
        settings,
        persistent,
      })
    );
  };

export const getLeftMenu = (
  viewState: PagesViewSettings,
  incomingReferenceTypes: TraversedReferenceTypeInfo[],
  outgoingReferenceTypes: TraversedReferenceTypeInfo[],
  workspaceFields: (Pick<APIFieldAttributes, 'name' | 'label'> & {
    deselectedByDefault?: boolean;
  })[],
  onViewSettingsUpdate: OnViewSettingsUpdate,
  i18nLocale: I18nLocale
): SettingsConfig[] => {
  const {
    hasRefTypeOptions: hasIncomingReferenceTypes,
    referenceTypeGroup: incomingReferenceTypeOptions,
  } = getDropdownOptionGroup<TraversedReferenceTypeInfo, PagesViewSettings>({
    viewId: VIEW_ID,
    availableRefTypes: incomingReferenceTypes,
    key: 'includeIncomingReferenceTypes',
    viewState,
    isActive: (referenceType: TraversedReferenceTypeInfo) =>
      referenceOptionIsActive(
        referenceType,
        viewState.includeIncomingReferenceTypes,
        incomingReferenceTypes
      ),
    onClick: getSelectedReferenceOnClick(
      'includeIncomingReferenceTypes',
      viewState.includeIncomingReferenceTypes,
      incomingReferenceTypes
    ),
    formatValue: formatReferenceType,
    metaOptions: MenuMetaOptions.NO_OPTIONS,
    i18nLocale,
  });

  const incomingAllNone = getAllNoneDropdownGroup<
    TraversedReferenceTypeInfo,
    PagesViewSettings
  >({
    viewId: VIEW_ID,
    viewState,
    key: 'includeIncomingReferenceTypes',
    availableOptions: incomingReferenceTypes,
    isActive: (commonDropdownOption: CommonDropdownOptions) =>
      referenceOptionIsActive(
        commonDropdownOption,
        viewState.includeIncomingReferenceTypes,
        incomingReferenceTypes
      ),
    onClick: getSelectedReferenceOnClick(
      'includeIncomingReferenceTypes',
      viewState.includeIncomingReferenceTypes,
      incomingReferenceTypes
    ),
    i18nLocale,
  });

  const {
    hasRefTypeOptions: hasOutgoingReferenceTypes,
    referenceTypeGroup: outgoingReferenceTypeOptions,
  } = getDropdownOptionGroup<TraversedReferenceTypeInfo, PagesViewSettings>({
    viewId: VIEW_ID,
    availableRefTypes: outgoingReferenceTypes,
    key: 'includeOutgoingReferenceTypes',
    viewState,
    isActive: (referenceType: TraversedReferenceTypeInfo) =>
      referenceOptionIsActive(
        referenceType,
        viewState.includeOutgoingReferenceTypes,
        outgoingReferenceTypes
      ),
    onClick: getSelectedReferenceOnClick(
      'includeOutgoingReferenceTypes',
      viewState.includeOutgoingReferenceTypes,
      outgoingReferenceTypes
    ),
    formatValue: formatReferenceType,
    metaOptions: MenuMetaOptions.NO_OPTIONS,
    i18nLocale,
  });

  const outgoingAllNone = getAllNoneDropdownGroup<
    TraversedReferenceTypeInfo,
    PagesViewSettings
  >({
    viewId: VIEW_ID,
    viewState,
    key: 'includeOutgoingReferenceTypes',
    availableOptions: outgoingReferenceTypes,
    isActive: (commonDropdownOption: CommonDropdownOptions) =>
      referenceOptionIsActive(
        commonDropdownOption,
        viewState.includeOutgoingReferenceTypes,
        outgoingReferenceTypes
      ),
    onClick: getSelectedReferenceOnClick(
      'includeOutgoingReferenceTypes',
      viewState.includeOutgoingReferenceTypes,
      outgoingReferenceTypes
    ),
    i18nLocale,
  });

  const outgoingGroup = hasOutgoingReferenceTypes
    ? [
        {
          label: t(
            i18nLocale,
            'settingsBar:outgoingReferenceTypes',
            'Outgoing reference types'
          ),
          type: DropdownOptionType.HEADER,
        },
        ...outgoingAllNone,
        ...outgoingReferenceTypeOptions,
      ]
    : [];

  const incomingGroup = hasIncomingReferenceTypes
    ? [
        {
          label: t(
            i18nLocale,
            'settingsBar:incomingReferenceTypes',
            'Incoming reference types'
          ),
          type: DropdownOptionType.HEADER,
        },
        ...incomingAllNone,
        ...incomingReferenceTypeOptions,
      ]
    : [];

  return [
    {
      id: 'toggleFields',
      label: t(
        i18nLocale,
        'tabview:pagesView.menu.selectFields',
        'Select fields'
      ),
      iconName: IconName.FORMAT_LIST_BULLETED,
      options: getFieldMultiSelectDropdownOptions(
        VIEW_ID,
        workspaceFields,
        viewState as Pick<PagesViewSettings, 'includeFields'>,
        FIELD_SETTINGS_KEY,
        onViewSettingsUpdate,
        i18nLocale
      ),
      type: SettingsType.DROPDOWN,
      isDisabled: false,
      isKeepOpen: true,
    },
    {
      id: 'toggleReferenceTypes',
      label: t(
        i18nLocale,
        'tabview:pagesView.menu.selectReferenceTypes',
        'Select reference types'
      ),
      iconName: IconName.REFERENCE,
      options: [...outgoingGroup, ...incomingGroup],
      type: SettingsType.DROPDOWN,
      isDisabled: !hasIncomingReferenceTypes && !hasOutgoingReferenceTypes,
      isKeepOpen: true,
    },
    {
      id: 'hideEmptyFields',
      label: viewState.hideEmptyFields
        ? t(
            i18nLocale,
            'tabview:pagesView.menu.showEmptyFields',
            'Show empty fields'
          )
        : t(
            i18nLocale,
            'tabview:pagesView.menu.hideEmptyFields',
            'Hide empty fields'
          ),
      iconName: viewState.hideEmptyFields
        ? IconName.SPACE_BAR
        : IconName.SPACE_BAR_CROSSED,
      type: SettingsType.TOGGLE,
      isActive: viewState.hideEmptyFields,
      isDisabled: false,
      onViewSettingsUpdate,
    },
    {
      id: 'expandDescription',
      label: viewState.expandDescription
        ? t(
            i18nLocale,
            'tabview:pagesView.menu.collapseDescriptions',
            'Collapse descriptions'
          )
        : t(
            i18nLocale,
            'tabview:pagesView.menu.expandDescriptions',
            'Expand descriptions'
          ),
      iconName: viewState.expandDescription
        ? IconName.UNFOLD_LESS
        : IconName.UNFOLD_MORE,
      type: SettingsType.TOGGLE,
      isActive: viewState.expandDescription,
      isDisabled: false,
      onViewSettingsUpdate,
    },
  ];
};

type GetRightMenuArgs = {
  viewSettings: PagesViewSettings;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  isEmptyView: boolean;
  printOrSaveToPdf: () => void;
  isLegendPossible: boolean;
  i18nLocale: I18nLocale;
};

export const getRightMenu = ({
  viewSettings,
  onViewSettingsUpdate,
  isEmptyView,
  printOrSaveToPdf,
  isLegendPossible,
  i18nLocale,
}: GetRightMenuArgs) =>
  getRightMenuConfig({
    viewId: VIEW_ID,
    viewstate: viewSettings,
    exports: {
      printOrSaveToPdf,
      addToPresentation: focusedChromeOperations.allowsAddToPresentation(
        focusedChromeNow()
      )
        ? () => addToPresentation(VIEW_ID, false)
        : undefined,
      isDisabled: isEmptyView,
    },
    withLegend: viewHasLegend(VIEW_ID),
    knowledgeBaseLink: KnowledgeBaseLink.PAGES,
    onViewSettingsUpdate,
    i18nLocale,
  }).map(setting =>
    setting.id === viewSettingsConsts.IS_LEGEND_ACTIVE
      ? {
          id: viewSettingsConsts.IS_LEGEND_ACTIVE,
          label: t(
            i18nLocale,
            'tabview:pagesView.menu.toggleLegend',
            'Toggle legend'
          ),
          iconName: IconName.INFO,
          isActive: viewSettings[viewSettingsConsts.IS_LEGEND_ACTIVE],
          isDisabled: !isLegendPossible || isEmptyView,
          popoverText: !isLegendPossible
            ? t(
                i18nLocale,
                'tabview:pagesView.menu.legendImpossiblePopover',
                'Legend is available when a conditional format is added in Perspectives'
              )
            : undefined,
          persistent: false,
          type: SettingsType.TOGGLE,
          onViewSettingsUpdate,
        }
      : setting
  );
